/**
 * Season-deadline pricing (founder-approved 2026-08-04): the Season Pass is
 * $99 through September 30, $149 from October 1.
 *
 * THE DEADLINE IS REAL. payment_manager_v2 enforces the same instant
 * server-side (SEASON_PASS_DEADLINE_UTC there — keep the two in sync by
 * hand), so the urgency line on the pricing page is a fact, not a bluff:
 * a stale tab or an edited client still pays the server's price. This file
 * only controls what we DISPLAY; the server controls what we CHARGE.
 *
 * The instant is Sep 30 2026 23:59:59 Pacific, expressed in UTC so client
 * clocks in any timezone agree with the server.
 */
export const SEASON_PASS_DEADLINE_UTC = '2026-10-01T06:59:59Z';
export const SEASON_PASS_EARLY_USD = 99;
export const SEASON_PASS_REGULAR_USD = 149;
// Human-readable, used in copy. Plain date — deliberately no countdown widget.
export const SEASON_PASS_DEADLINE_LABEL = 'September 30';

export function isEarlySeason(now = new Date()) {
    return now.getTime() <= Date.parse(SEASON_PASS_DEADLINE_UTC);
}

export function seasonPassPriceUsd(now = new Date()) {
    return isEarlySeason(now) ? SEASON_PASS_EARLY_USD : SEASON_PASS_REGULAR_USD;
}

/**
 * The $15 Monthly subscription retires at the same instant the Pass steps up
 * (ADR 0013, founder 2026-09-21: "lets scrap the monthly on October 1st").
 * One date in the code, not two.
 *
 * Why: a monthly figure sells Stratia in the apps-and-subscriptions mental
 * account, where $15 a month reads as expensive across a year; a season sells
 * it beside a counselor, where $149 is a rounding error. Nobody had bought
 * Monthly, so nothing is grandfathered.
 *
 * payment_manager_v2 refuses the SALE at the same instant
 * (`monthly_retired()` there). The pricing page and the upgrade modal stopped
 * OFFERING Monthly earlier, on 2026-09-22 (ADR 0013 amendment); this clock
 * still governs the Terms, which describe the plan while anyone can be on it.
 */
/** The legacy Monthly plan's price (ADR 0013): no longer sold, shown only
 * to a current Monthly holder on /pricing as the plan they pay for. One home,
 * so the holder's panel and the Terms cannot disagree. */
export const LEGACY_MONTHLY_USD = 15;

export function monthlyRetired(now = new Date()) {
    return !isEarlySeason(now);
}

/** The urgency line for the pricing page — '' once the deadline passes, so
 * stale copy can never advertise a price that is no longer offered. */
export function seasonDeadlineLine(now = new Date()) {
    return isEarlySeason(now)
        ? `Season pricing: $${SEASON_PASS_EARLY_USD} through ${SEASON_PASS_DEADLINE_LABEL} — then $${SEASON_PASS_REGULAR_USD}.`
        : '';
}

/**
 * A market's own Season Pass price (#4966, founder 2026-10-20): India sees
 * the Pass at Rs 9,999, shown AND charged in rupees, behind the india_masters
 * flag. This is the page's figure, in whole rupees; payment_manager_v2
 * charges regions.py SEASON_PASS_INR_PAISE (pinned equal to this x 100 by
 * test_regions.py). The page DISPLAYS; the server CHARGES, and only to a
 * card issued in India (the payment is held until the card says so, ADR 0060).
 */
export const SEASON_PASS_INR = 9999;

/**
 * The Season Pass through graduation (#4986, founder 2026-10-21: "Yes" to a
 * one-time pass at $299). One payment, every season until the student's
 * applications are done (through June 30 of their high-school graduation
 * year, or a year from purchase if that is later, #5239), offered to grades 9 to 12 behind one_payment_passes (founder
 * 2026-10-23: "9 to 12 not 9 to 11"; the server's pass_terms.OFFERED_GRADES
 * is the one definition). The page's
 * figure; payment_manager_v2 charges products.py THROUGH_GRADUATION_CENTS
 * (pinned equal to this x 100 by test_pass_terms.py) and derives the end
 * date itself, from the student's profile, never from the page.
 */
export const THROUGH_GRADUATION_USD = 299;

/**
 * Through Graduation in India (#4986, founder 2026-10-21: "we can launch
 * india now.. for both undergrad and masters we show the pricing
 * accordingly"; Rs 19,999 confirmed). Whole rupees; payment_manager_v2
 * charges regions.py THROUGH_GRADUATION_INR_PAISE (pinned equal to this x 100
 * by test_pass_terms.py), held for an India-issued card (ADR 0060), behind
 * india_masters. The page shows the server's figure for the visitor's region.
 */
export const THROUGH_GRADUATION_INR = 19999;

/**
 * A price as the market reads it: "$149" in the US, "₹9,999" in India (the
 * en-IN grouping, no paise). Whole units only: every price here is one.
 */
export function formatPrice(amount, currency = 'usd', locale = 'en-US') {
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency', currency: String(currency).toUpperCase(),
            minimumFractionDigits: 0, maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${String(currency).toUpperCase()} ${amount}`;
    }
}
